import { useState, useEffect, useMemo, useRef } from 'react'
import { t } from 'i18next'
import DataTable from 'react-data-table-component'
import { FormGroup, Label, Input } from 'reactstrap'
import moment from 'moment'

import { isNull, customStyles, paginationComponentOptions } from '../../../izUtils'
import { FilterComponentText, FilterComponentSelect } from '../../../components/task/list/FilterComponent'
import TechnicianDateRange from '../../../components/task/list/TechnicianDateRange'

const EventTechnicians = ({ data, saveToLS, showErrors, updateData, eventFrom, eventTo, autoAssign, setAutoAssign }) => {
    const [selectedRows, setSelectedRows] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [sameDate, setSameDate] = useState(true);

    const [filterText, setFilterText] = useState("");
    const [filterDepartment, setFilterDepartment] = useState("");

    const firstTime = useRef(true);

    useEffect(() => {
        let selected = [];
        const tableTechnicians = data.values.map(row => {
            if (!isNull(data.value) && data.value.length !== 0) {
                if (Array.isArray(data.value)) {
                    let findTech = data.value.find(tech => tech.id === row.id);
                    if (findTech) {
                        selected.push(findTech)
                    }
                }
            }

            return {...row}
        })

        // Sort tableTechnicians to put selected technicians at the top
        const sortedTechnicians = tableTechnicians.sort((a, b) => {
            const aIsSelected = selected.find(tech => tech.id === a.id);
            const bIsSelected = selected.find(tech => tech.id === b.id);

            if (aIsSelected && !bIsSelected) return -1;
            if (!aIsSelected && bIsSelected) return 1;
            return 0;
        });

        setSelectedRows(selected)
        localStorage.setItem(saveToLS, JSON.stringify(selected))
        setTableData(sortedTechnicians)

    }, [JSON.stringify(data.value)]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (firstTime.current) {
            firstTime.current = false;
            return;
        }

        if (sameDate) {
            let clonedSelectedRows = [...selectedRows];
            clonedSelectedRows.map(selected => {
                selected.assigned = [{ from: eventFrom, to: eventTo }];
                return selected;
            })

            setSelectedRows(clonedSelectedRows)
            localStorage.setItem(saveToLS, JSON.stringify(clonedSelectedRows))
        }
    }, [sameDate, eventFrom, eventTo]) // eslint-disable-line react-hooks/exhaustive-deps

    // Create table columns
    const tableColumns = useMemo(() => {
        let columns = [
            { name: t('table.name'), selector: row => row.title, sortable: true },
            {
                name: t('table.dates'),
                cell: row => {
                    let isSelected = selectedRows.find(selected => selected.id === row.id);
                    return (
                        <div>
                            {(data.disabled || isSelected) &&
                                isSelected?.assigned.map((element, index) => {
                                    return (
                                        <div key={row.id+'-'+index} className='d-flex flex-wrap mb-2'>
                                            <TechnicianDateRange
                                                index={index}
                                                element={element}
                                                row={row}
                                                disabled={sameDate || data.disabled}
                                                selectedRows={selectedRows}
                                                handleAssigned={handleAssigned}
                                                localStorageName={saveToLS}
                                                serviceFrom={eventFrom}
                                                serviceTo={eventTo}
                                            />
                                        </div>
                                    )
                                })
                            }
                            {(!data.disabled && !sameDate) && <button className="btn btn-primary btn-sm py-1 px-2" disabled={!isSelected} onClick={() => addNewAssigned(row)}>{t('addDate')}</button>}
                        </div>
                    )
                }
            },
        ]

        if (!data.disabled) {
            columns.unshift(
                {
                    name: '',
                    width: '50px',
                    cell: row => {
                        let isSelected = selectedRows.find(selected => selected.id === row.id)
                        return (
                            <div className="custom-control custom-checkbox form-check">
                                <div className="form-checkbox-group">
                                    <Input
                                        className={"custom-control-input"}
                                        id={row.id + '-' + saveToLS}
                                        type="checkbox"
                                        onChange={() => handleRowSelected(row)}
                                        checked={!!isSelected}
                                    />
                                    <Label className="checkmark secondary" htmlFor={row.id + '-' + saveToLS}></Label>
                                </div>
                            </div>
                        )
                    }
                },
            )
        }

        return columns;
    }, [selectedRows, data.disabled, sameDate, eventFrom, eventTo]) // eslint-disable-line react-hooks/exhaustive-deps

    // Row handlers
    const handleRowSelected = (row) => {
        let clonedSelectedRows = [...selectedRows];
        let isSelected = clonedSelectedRows.find(selected => selected.id === row.id);
        let lsEventData = JSON.parse(localStorage.getItem('AKODA.eventInputs'));

        if (!isSelected) {
            clonedSelectedRows.push({
                id: row.id,
                assigned: sameDate ?
                    [
                        {
                            from: lsEventData?.from?.value && lsEventData?.from?.value !== '' ? lsEventData?.from?.value : null,
                            to: lsEventData?.to?.value && lsEventData?.to?.value !== '' ? lsEventData?.to?.value : null
                        }
                    ]
                    :
                    []
            })
        } else {
            let filtered = clonedSelectedRows.filter(selected => selected.id !== row.id)
            clonedSelectedRows = filtered;
        }

        setSelectedRows(clonedSelectedRows)
        localStorage.setItem(saveToLS, JSON.stringify(clonedSelectedRows))
        updateData()
    }

    const addNewAssigned = (row) => {
        let clonedSelectedRows = [...selectedRows];

        clonedSelectedRows.map(selected => {
            if (selected.id === row.id) {
                selected.assigned.push({ from: '', to: '' });
            }

            return selected;
        });

        setSelectedRows(clonedSelectedRows)
        localStorage.setItem(saveToLS, JSON.stringify(clonedSelectedRows))
    }

    const handleAssigned = (type, row, index, val) => {
        let clonedSelectedRows = [...selectedRows];

        if (!isNull(clonedSelectedRows)) {
            if (type === 'start' || type === 'end') {
                clonedSelectedRows.map((selectedTech) => {
                    if (selectedTech.id === row.id) {
                        if (isNull(selectedTech.assigned[index])) {
                            selectedTech.assigned[index] = {}
                        }

                        if (type === 'start') {
                            selectedTech.assigned[index].from = moment.utc(val).format()
                        } else {
                            selectedTech.assigned[index].to = moment.utc(val).format()
                        }
                    }

                    return selectedTech;
                })
            } else if (type === 'delete') {
                clonedSelectedRows.map((selectedTech) => {
                    if (selectedTech.id === row.id) {
                        selectedTech.assigned.splice(index, 1);
                    }

                    return selectedTech;
                })
            }
        }

        setSelectedRows(clonedSelectedRows)
        localStorage.setItem(saveToLS, JSON.stringify(clonedSelectedRows))
    }
    // End row handlers

    const subHeaderComponent = useMemo(() => {
        const handleClear = (type) => {
            if (type === 'department') {
                setFilterDepartment("");
            } else if (type === 'text') {
                setFilterText("");
            }
        };

        const optionsDepartment = [
            { id: 'it_video', title: t('table.techDepartment.it_video') },
            { id: 'servicer', title: t('table.techDepartment.servicer') },
            { id: 'installer', title: t('table.techDepartment.installer') },
            { id: 'electrician', title: t('table.techDepartment.electrician') },
            { id: 'external_contractor', title: t('table.techDepartment.external_contractor') },
        ]

        return (
            <div className="w-100">
                <div className='d-flex flex-wrap justify-content-between align-items-end'>
                    <div className="d-flex flex-wrap align-items-center">
                        <FilterComponentSelect
                            options={optionsDepartment}
                            onFilter={e => setFilterDepartment(e.target.value)}
                            onClear={() => handleClear('department')}
                            filterValue={filterDepartment}
                            type="department"
                        />
                        <FilterComponentText
                            onFilter={e => setFilterText(e.target.value)}
                            onClear={() => handleClear('text')}
                            filterValue={filterText}
                        />
                    </div>
                    {!data.disabled &&
                    <div>
                        <div className='custom-control custom-checkbox form-check'>
                            <div className="form-checkbox-group">
                                <Input
                                    className={"custom-control-input"}
                                    id={"checkbox-same-date"}
                                    type="checkbox"
                                    onChange={() => {
                                        if (!sameDate) setAutoAssign(false);
                                        setSameDate(!sameDate)
                                    }}
                                    checked={sameDate}
                                />
                                <Label className="checkmark" htmlFor={"checkbox-same-date"}></Label>
                                <Label className="custom-control-label" htmlFor={"checkbox-same-date"} style={{ cursor: 'pointer' }}>{t("form.label.sameDates")}</Label>
                                <div className="invalid-feedback">{"Example invalid feedback text"}</div>
                            </div>
                        </div>
                        <div className='custom-control custom-checkbox form-check'>
                            <div className="form-checkbox-group">
                                <Input
                                    className={"custom-control-input"}
                                    id={"checkbox-auto-assign"}
                                    type="checkbox"
                                    disabled={sameDate}
                                    onChange={() => setAutoAssign(!autoAssign)}
                                    checked={autoAssign}
                                />
                                <Label className="checkmark" htmlFor={"checkbox-auto-assign"}></Label>
                                <Label className="custom-control-label" htmlFor={"checkbox-auto-assign"} style={{ cursor: 'pointer' }}>{t("form.label.autoAssign")}</Label>
                                <div className="invalid-feedback">{"Example invalid feedback text"}</div>
                            </div>
                        </div>
                    </div>
                    }
                </div>
            </div>
        );
      }, [filterText, filterDepartment, sameDate, autoAssign, data.disabled]); // eslint-disable-line react-hooks/exhaustive-deps

    let filteredItems = useMemo(() => {
        let filteredTableData = tableData.filter(item => {
            return (item.title && item.title.toLowerCase().includes(filterText.toLowerCase()))
        });

        filteredTableData = filteredTableData.filter(item => {
            return (filterDepartment === "" || (item?.department && item?.department.indexOf(filterDepartment) !== -1))
        });

        return filteredTableData;
    }, [tableData, filterText, filterDepartment])

    return (
        <FormGroup className={"table-with-filters"}>
            <Label>{data.title + (data.mandatory ? " *" : "")}</Label>
            {tableData.length !== 0 ?
                <DataTable
                    columns={tableColumns}
                    data={filteredItems}
                    pagination
                    paginationComponentOptions={paginationComponentOptions(t)}
                    customStyles={customStyles}
                    subHeader
                    subHeaderComponent={subHeaderComponent}
                />
                :
                <div>{t('table.noTechnicians')}</div>
            }
            {!isNull(showErrors) && showErrors && !isNull(data.errors) && data.errors.length !== 0 &&
                data.errors.map((error, index) => (
                    <div key={'material-error-'+index} style={{ color: "red", marginBottom: "10px" }}>{error}</div>
                ))
            }
        </FormGroup>
    )
}

export default EventTechnicians